import { signal } from '@angular/core';

export type AnchoredPanelPlacement = 'above' | 'below';

export type AnchoredPanelOptions = {
  placement: AnchoredPanelPlacement;
  gapPx: number;
  maxHeightPx: number;
  minHeightPx: number;
};

/**
 * Fixed-position panel anchored to an element (escapes overflow:hidden ancestors).
 * Shared by conversation filters (above) and prompt-hints dropdown (below).
 */
export class AnchoredPanelController {
  readonly isVisible = signal(false);
  readonly display = signal('none');
  readonly top = signal('auto');
  readonly bottom = signal('auto');
  readonly left = signal('0px');
  readonly width = signal('0px');
  readonly maxHeight = signal('0px');

  private visible = false;
  private anchor: HTMLElement | null = null;
  private listenersAttached = false;
  private readonly boundReposition = () => this.reposition();

  constructor(private readonly options: AnchoredPanelOptions) {
    this.maxHeight.set(`${options.maxHeightPx}px`);
  }

  update(visible: boolean, anchor: HTMLElement | null): void {
    this.visible = visible;
    this.anchor = anchor;

    const open = visible && !!anchor;
    this.isVisible.set(open);
    this.display.set(open ? 'block' : 'none');

    if (open) {
      this.attachListeners();
      requestAnimationFrame(() => this.reposition());
      return;
    }

    this.detachListeners();
  }

  destroy(): void {
    this.detachListeners();
  }

  private attachListeners(): void {
    if (this.listenersAttached) {
      return;
    }

    this.listenersAttached = true;
    window.addEventListener('resize', this.boundReposition);
    document.addEventListener('scroll', this.boundReposition, true);
  }

  private detachListeners(): void {
    if (!this.listenersAttached) {
      return;
    }

    this.listenersAttached = false;
    window.removeEventListener('resize', this.boundReposition);
    document.removeEventListener('scroll', this.boundReposition, true);
  }

  private reposition(): void {
    if (!this.visible || !this.anchor) {
      return;
    }

    const { placement, gapPx, maxHeightPx, minHeightPx } = this.options;
    const rect = this.anchor.getBoundingClientRect();
    const viewportPadding = 8;

    this.left.set(`${rect.left}px`);
    this.width.set(`${rect.width}px`);

    if (placement === 'above') {
      const spaceAbove = rect.top - gapPx - viewportPadding;
      this.top.set('auto');
      this.bottom.set(`${window.innerHeight - rect.top + gapPx}px`);
      this.maxHeight.set(
        `${Math.max(minHeightPx, Math.min(maxHeightPx, spaceAbove))}px`
      );
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom - gapPx - viewportPadding;
    this.top.set(`${rect.bottom + gapPx}px`);
    this.bottom.set('auto');
    this.maxHeight.set(
      `${Math.max(minHeightPx, Math.min(maxHeightPx, spaceBelow))}px`
    );
  }
}
